import { FunctionComponent, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { ErrorDetails, SpinnerWrapper } from "@components/Activity/styles/activity.styled";
import { useHeaderUpdater } from "@components/HeaderContextProvider";
import Notification from "@components/Notification";
import Player from "@components/Player";
import { usePlayerState } from "@components/PlayerLayout";
import { DialogTextFor, MaterialType } from "@domain/types";
import { assertIsDefined } from "@domain/utils";
import useActivity from "@hooks/useActivity";
import usePrefetchPlayLearningObject from "@hooks/usePrefetchPlayLearningObject";
import { getNotificationStructure } from "@utils/helpers";

import CenteredSpinner from "@topgun/shared-front-end/src/components/CenteredSpinner";

import { LearningObjectContext } from "../../event/dispatcher/ServerSideEventDispatcherContext";

type ActivityUrlProps = {
  activityId: string;
  pageId: string;
};

const Activity: FunctionComponent = () => {
  const { activityId, pageId } = useParams<ActivityUrlProps>();
  assertIsDefined(activityId, "Unknown activity ID");

  const { clearNavigateBack, setNavigateBack, setBreadcrumbs } = useHeaderUpdater();
  const { activity, pages, isLoading } = useActivity(activityId);
  const { progress, setProgress } = usePlayerState();
  const navigate = useNavigate();

  const activePageId = pageId || pages?.[0]?.value.id;
  const foundPage =
    activePageId && pages ? pages.find((page) => page.value.id === activePageId) : undefined;
  const learningObjectId = foundPage?.value.learningObjectId;
  const pageIndex = pages?.findIndex((page) => page.value.id === activePageId) || 0;

  const nextPage = pages?.[pageIndex + 1];
  const nextLearningObjectId = nextPage?.value.learningObjectId;

  usePrefetchPlayLearningObject(nextLearningObjectId);

  useEffect(() => {
    setProgress(pages ? [pageIndex + 1, pages.length] : undefined);
  }, [setProgress, pages, pageIndex]);

  useEffect(() => {
    const chapter = activity?.parent?.parent?.value;
    if (chapter) {
      setNavigateBack({
        to: `/chapters/${chapter.id}`,
        icon: "cross",
      });
    }
  }, [activity, setNavigateBack]);

  useEffect(
    () => () => {
      clearNavigateBack();
    },
    [clearNavigateBack],
  );

  useEffect(() => {
    if (activity) {
      const chapter = activity.parent?.parent?.value;
      if (chapter) {
        setBreadcrumbs([
          ...(chapter.sequenceLabel
            ? [
                {
                  label: `H.${chapter.sequenceLabel}`,
                  to: `/chapters/${chapter.id}`,
                },
              ]
            : []),
          {
            label: `${activity.value.sequenceLabel || ""} ${activity.value.title}`.trim(),
            to: `/activities/${activity.value.id}`,
            dialogTextFor: DialogTextFor.RESTART,
            disabled: progress && progress[0] === 1,
          },
        ]);
      }
    }
  }, [activity, setBreadcrumbs, progress]);

  if (learningObjectId) {
    const goToPreviousPage = () => {
      if (!pages?.length || pageIndex === 0) {
        return;
      }

      const previousPage = pages[pageIndex - 1];
      const { id: previousPageId } = previousPage.value;

      navigate(`/activities/${activityId}/page/${previousPageId}`);
    };

    const goToNextPage = () => {
      if (!pages?.length) {
        return;
      }
      if (pages && pageIndex === pages.length - 1) {
        navigate(`/activities/${activityId}/checkout`);
        return;
      }
      const nextPageId = pages[pageIndex + 1].value.id;

      navigate(`/activities/${activityId}/page/${nextPageId}`);
    };

    const learningObjectContext: LearningObjectContext = {
      context: activityId,
      contextType: "ACTIVITY",
      learningObjectId,
    };

    return (
      <Player
        contentReference={foundPage.value.title}
        pageIndex={pageIndex}
        onPreviousClick={goToPreviousPage}
        learningObjectContext={learningObjectContext}
        onNextClick={goToNextPage}
      />
    );
  }

  if (isLoading) {
    return (
      <SpinnerWrapper>
        <CenteredSpinner />
      </SpinnerWrapper>
    );
  }

  const error = getNotificationStructure(MaterialType.ACTIVITY);
  return (
    <Notification $alignNotification variant={error.variant} title={error.title}>
      {error.children}
      {pages?.length === 0 && <ErrorDetails>EMPTY_ACTIVITY: {activityId}</ErrorDetails>}
    </Notification>
  );
};

export default Activity;
